import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";

import Events from "./Events";
import Filters from "./Filters";
import Sidebar from "./Sidebar";
import Stats from "./Stats";
import Videos from "./Videos";

export default function App(): JSX.Element {
    const [errorMessage, setErrorMessage] = useState("");

    const [data, setData] = useState<Summary | null>(null);
    useEffect(() => {
        function fetchSummary() {
            fetch(`/data/${window.location.pathname.replaceAll("/", "")}/summary.json`)
                .then((res) => {
                    if (!res.ok) {
                        return Promise.reject(new Error("Match not found"));
                    }
                    return res.json();
                })
                .then((raw: RawSummary) => setData(summaryFromRaw(raw)))
                .catch((err: Error) => { setErrorMessage(err.message); });
        }

        fetchSummary();

        window.addEventListener("popstate", fetchSummary);
        return () => {
            window.removeEventListener("popstate", fetchSummary);
        };
    }, []);

    const [selectedShot, setSelectedShot] = useState<Point | null>(null);
    const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
        player: [],
        outcome: [],
        shot: [],
    });
    const [showStats, setShowStats] = useState(false);

    const onFilter = useCallback((newFilters: SelectedFilters) => {
        setSelectedFilters(newFilters);
        setShowStats(false);
    }, []);

    const filteredData = useMemo(() => {
        if (!selectedFilters.outcome.length && !selectedFilters.player.length && !selectedFilters.shot.length) {
            return data?.points ?? [];
        }

        return data?.points.filter((shot) => {
            let outcomePredicate = true;
            if (selectedFilters.outcome.length) {
                outcomePredicate = selectedFilters.outcome.some((result) => shot.outcome.result === result);
            }

            let playerPredicate = true;
            if (selectedFilters.player.length) {
                playerPredicate = selectedFilters.player.some((player) => shot.outcome.player === player);
            }

            let shotPredicate = true;
            if (selectedFilters.shot.length) {
                shotPredicate = selectedFilters.shot.some((shotType) => shot.outcome.shot === shotType);
            }

            return outcomePredicate && playerPredicate && shotPredicate;
        }) ?? [];
    }, [selectedFilters, data?.points]);

    if (errorMessage) {
        return <p className="bg-red-700 p-4 text-center text-lg text-white">{errorMessage}</p>;
    }

    if (!data) {
        return <p className="bg-blue-zodiac-500 p-4 text-center text-lg">Loading…</p>;
    }

    return (
        <>
            <main className="grid grid-cols-12 gap-4 p-4 text-white">
                <div className="col-span-2 flex flex-col">
                    <Sidebar
                        date={data.date}
                        venue={data.venue}
                        onShowStats={() => setShowStats(true)}
                    />
                </div>
                <div className="col-span-10">
                    <Videos
                        centerUrl={data.center_video}
                        rightUrl={data.right_video}
                        leftUrl={data.left_video}
                        selectedShot={selectedShot}
                    />
                </div>
                <div className="col-span-2" />
                <div className="col-span-4">
                    <Filters
                        players={data.players}
                        selectedFilters={selectedFilters}
                        onChange={(f) => setSelectedFilters(f)}
                    />
                </div>
                <div className="col-span-6">
                    <Events
                        points={filteredData}
                        selectedShot={selectedShot}
                        onClick={(shot: Point | null) => {
                            setSelectedShot(shot);
                            window.scrollTo(0, 0);
                        }}
                    />
                </div>
            </main>
            <Stats summary={data} open={showStats} onFilter={onFilter} onClose={() => setShowStats(false)} />
        </>
    );
}

function summaryFromRaw(raw: RawSummary): Summary {
    const shots: Point[] = raw.shots.map((shot, i) => ({
        ...shot,
        score: raw.score[i],
        serve_player: raw.serve_player[i],
        serve: raw.serve[i],
        outcome: raw.outcome[i],
    }));

    return {
        points: shots,
        venue: raw.venue,
        date: new Date(raw.date),
        players: raw.players,
        frame_rate: raw.frame_rate,
        center_video: raw.center_video,
        right_video: raw.right_video,
        left_video: raw.left_video,
        duration: raw.duration,
        frames: raw.frames,
    };
}
